import { DataTable } from '@/components/DataTable';
import { Badge, Card, PageHeader } from '@/components/ui';
import { formatDateTime } from '@/lib/format';

interface AuditLog {
  id: number;
  tenant_id: string | null;
  client_name: string | null;
  actor_type: string;
  actor_email: string | null;
  action: string;
  entity: string | null;
  entity_id: string | null;
  ip: string | null;
  created_at: string;
}

export default function AuditLogs() {
  return (
    <div className="w-full">
      <PageHeader
        title="Audit logs"
        description="Every mutating action, with who did it and from where."
      />

      <Card className="mb-4">
        <p className="text-sm text-ink-600 dark:text-ink-300">
          Before and after snapshots are captured for each change, with secret-shaped values
          redacted before they are written — an audit row can never become a way to read a
          stored credential.
        </p>
      </Card>

      <DataTable<AuditLog>
        queryKey="admin-audit-logs"
        path="/admin/audit-logs"
        searchable={false}
        pageSize={100}
        emptyTitle="No audit entries yet"
        columns={[
          {
            key: 'created_at',
            header: 'When',
            render: (log) => (
              <span className="whitespace-nowrap text-xs text-ink-500">
                {formatDateTime(log.created_at)}
              </span>
            ),
          },
          {
            key: 'actor',
            header: 'Actor',
            render: (log) => (
              <div>
                <div className="text-sm">{log.actor_email ?? '—'}</div>
                <Badge tone={log.actor_type === 'platform' ? 'purple' : 'gray'}>
                  {log.actor_type}
                </Badge>
              </div>
            ),
          },
          {
            key: 'action',
            header: 'Action',
            render: (log) => (
              <span className="font-mono text-xs text-ink-800 dark:text-ink-100">
                {log.action}
              </span>
            ),
          },
          {
            key: 'entity',
            header: 'Entity',
            render: (log) => (
              <div>
                <div className="text-xs">{log.entity ?? '—'}</div>
                {log.entity_id && (
                  <div className="font-mono text-[10px] text-ink-400">{log.entity_id}</div>
                )}
              </div>
            ),
          },
          {
            key: 'client',
            header: 'Client',
            render: (log) => log.client_name ?? <span className="text-ink-400">Platform</span>,
          },
          {
            key: 'ip',
            header: 'IP',
            render: (log) => (
              <span className="font-mono text-xs text-ink-500">{log.ip ?? '—'}</span>
            ),
          },
        ]}
      />
    </div>
  );
}
